import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import dayjs from 'dayjs'

import { useDecisionText } from '@/components/utils/judicialDecision'
import { downloadCsv } from '@/lib/csv'

import { type JudicialRecord } from '../../model'

export function useExportJudicialRecordsCsv() {
  const intl = useIntl()

  const getDecisionText = useDecisionText()

  const getStatusText = useCallback(
    (status: JudicialRecord['municipalSearchStatus']) => {
      switch (status) {
        case 'completed':
          return intl.formatMessage({ id: 'judicial.results.status.completed' })
        case 'processing':
          return intl.formatMessage({ id: 'judicial.results.status.processing' })
        case 'retrieving':
          return intl.formatMessage({ id: 'judicial.results.status.retrieving' })
        case 'pending':
          return intl.formatMessage({ id: 'judicial.results.status.pending' })
        case 'error':
          return intl.formatMessage({ id: 'judicial.results.status.error' })
        default:
          return ''
      }
    },
    [intl]
  )

  const exportCsv = useCallback(
    (records: JudicialRecord[]) => {
      const headers = [
        intl.formatMessage({ id: 'judicial.results.table.volunteer' }),
        intl.formatMessage({ id: 'judicial.results.csv.lastName' }),
        intl.formatMessage({ id: 'judicial.results.csv.firstName' }),
        intl.formatMessage({ id: 'judicial.results.table.birthDate' }),
        intl.formatMessage({ id: 'judicial.results.table.municipalSearchStatus' }),
        intl.formatMessage({ id: 'judicial.results.csv.municipalResultsCount' }),
        intl.formatMessage({ id: 'judicial.results.table.provincialSearchStatus' }),
        intl.formatMessage({ id: 'judicial.results.csv.provincialResultsCount' }),
        intl.formatMessage({ id: 'judicial.results.table.decision' }),
        intl.formatMessage({ id: 'judicial.results.table.lastDecision' }),
        intl.formatMessage({ id: 'judicial.results.table.requestTimestamp' }),
        intl.formatMessage({ id: 'judicial.results.csv.expirationDate' }),
        intl.formatMessage({ id: 'judicial.results.csv.autoAccepted' }),
      ]

      const rows = records.map((record) => [
        record.volunteer.code,
        record.volunteer.lastName,
        record.volunteer.firstName,
        record.volunteer.birthDate ? dayjs(record.volunteer.birthDate).format('YYYY-MM-DD') : '',
        getStatusText(record.municipalSearchStatus),
        String(record.municipal?.numberOfResults ?? 0),
        getStatusText(record.provincialSearchStatus),
        String(record.provincial?.numberOfResults ?? 0),
        getDecisionText(record.decision),
        record.lastDecisionDate ? dayjs(record.lastDecisionDate).format('YYYY-MM-DD') : '',
        dayjs(record.requestTimestamp).format('YYYY-MM-DD HH:mm'),
        dayjs(record.expirationDate).format('YYYY-MM-DD'),
        record.soquijAutoAcceptedAt ? dayjs(record.soquijAutoAcceptedAt).format('YYYY-MM-DD HH:mm') : '',
      ])

      const filename = `judicial-records-${dayjs().format('YYYY-MM-DD')}.csv`
      downloadCsv([headers, ...rows], filename)
    },
    [intl, getDecisionText, getStatusText]
  )

  return exportCsv
}
